import { FormEvent, Fragment, useCallback, useEffect, useState } from 'react';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';
import { ErrorMessage } from 'ui/shared/messages';
import { NoneProvided } from 'ui/shared/noneProvided';
import { BuildingId } from 'shared/models';
import { toError } from 'shared/util/toError';

require('./ExternalIdField.css');

export type Props = {
    buildingId: BuildingId | null;
    externalId: string;
    editing?: boolean;
    saving?: boolean;
    errorMsg?: string | null;
    changeExternalId(value: string): Promise<void>;
};

export const ExternalIdField = ({ buildingId, externalId, editing = false, saving, errorMsg = null, changeExternalId }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const [ value, setValue ] = useState(externalId);
    const [ isEditing, setIsEditing ] = useState(editing);
    const [ errorMessage, setErrorMessage ] = useState<string | null>(errorMsg);

    //
    // Reset our internal state when the input external ID changes
    //
    useEffect(() => {
        setValue(externalId);
        setIsEditing(false);
        setErrorMessage(null);
    }, [ externalId, setValue, setIsEditing, setErrorMessage ]);

    const handleChangeValue = useCallback((e: FormEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget;
        setValue(value.toUpperCase());
    }, [ setValue ]);

    const handleClickEdit = useCallback(() => {
        setIsEditing(true);
    }, [ setIsEditing ]);

    const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        void (async () => {
            try {
                setErrorMessage(null);
                await changeExternalId(value);
                setIsEditing(false);
            }
            catch (e) {
                const err = toError(e);
                setErrorMessage(t([ `ELEVATOR.ERROR.${err.message}`, 'SERVER_ERROR' ]));
            }
        })();
        e.preventDefault();
    }, [ value, changeExternalId, t, setIsEditing, setErrorMessage ]);
    
    const handleClickCancel = useCallback(() => {
        setValue(externalId);
        setIsEditing(false);
    }, [ externalId, setValue, setIsEditing ]);
    

    if (isEditing && hasPermission(null, Permission.ManageOrgExternalIds)) {
        return (
            <form
                id="external-id-field"
                className="ui form"
                onSubmit={ handleSubmit }
            >
                <input
                    type="text"
                    id="external-id"
                    maxLength={ 36 }
                    autoFocus
                    value={ value }
                    onChange={ handleChangeValue }
                    disabled={ saving }
                />
                { !saving &&
                    <button
                        type="submit"
                        className="save-button ui tiny icon button"
                        disabled={ saving }
                    >
                        <i className="ui grey check icon"/>
                    </button>
                }
                { !saving &&
                    <button
                        type="button"
                        className="cancel-button ui tiny icon button"
                        onClick={ handleClickCancel }
                        disabled={ saving }
                    >
                        <i className="ui grey cancel icon"/>
                    </button>
                }
                { saving &&
                    <LoadingSpinner size="tiny" textKey="SAVING"/>
                }
                { errorMessage &&
                    <ErrorMessage
                        message={ errorMessage }
                        onClick={ () => setErrorMessage(null) }
                    />
                }
            </form>
        );
    }
    else {
        return (
            <Fragment>
                { externalId || <NoneProvided/> }
                { hasPermission(null, Permission.ManageOrgExternalIds) &&
                    <button
                        type="button"
                        className="edit-button ui tiny icon button"
                        onClick={ handleClickEdit }
                    >
                        <i className="ui grey pencil icon"/>
                    </button>
                }
            </Fragment>
        );
    }
};
